// Mock data for Christina Clark Consulting website

export const heroData = {
  tagline: "Strategic Operations & Training Solutions",
  headline: "Bringing Clarity, Structure & Momentum",
  subheadline: "Partnering with property management executive teams to strengthen operations, develop people, and build sustainable practices that support performance, consistency, and growth.",
  ctaPrimary: "Schedule Consultation",
  ctaSecondary: "Our Services"
};

export const introData = {
  title: "Practical Strategy That Works in the Real World",
  description: "In today's property management environment, leaders are constantly balancing growth, compliance, staffing challenges, and customer expectations while managing day-to-day operational demands. Without the right systems, training, and alignment, even the most capable teams can find themselves stuck in reactive mode.",
  highlight: "I believe the bench strength of an organization provides a foundation to balance and build the future of an organization.",
  focus: "I specialize in reviewing operational challenges and recommending solutions, strategic planning with goal setting, and customized training designed specifically for property management organizations. Together we can move your leadership teams from \"putting out fires\" to operating with confidence, clarity, and purpose."
};

export const aboutPreviewData = {
  title: "Our Approach",
  subtitle: "Effective management is about more than systems—it's about people",
  features: [
    {
      icon: "Target",
      title: "Vision & Execution Alignment",
      description: "Bridging the gap between strategic goals and daily operations for consistent results."
    },
    {
      icon: "Users",
      title: "Relationship Focus",
      description: "Building connections that motivate teams and strengthen organizational culture."
    },
    {
      icon: "MessageCircle",
      title: "Coaching & Mentoring",
      description: "Thoughtful guidance that develops confident, capable leaders at all levels."
    },
    {
      icon: "Zap",
      title: "Disciplined Momentum",
      description: "Creating sustainable progress through focused, consistent action."
    }
  ]
};

export const servicesData = {
  title: "Core Services",
  subtitle: "Comprehensive solutions designed specifically for property management organizations",
  services: [
    {
      id: "operations",
      icon: "Briefcase",
      title: "Strategic Operations & Planning",
      description: "Strong organizations are built with clear direction and aligned execution.",
      features: [
        "Review and refine mission, vision, and organizational priorities",
        "Develop practical policies, procedures, and operational frameworks",
        "Align people, processes, and resources with business objectives",
        "Create clarity around roles, accountability, and execution"
      ],
      tagline: "Build your organization to support your goals and mission."
    },
    {
      id: "training",
      icon: "GraduationCap",
      title: "Training & Workforce Development",
      description: "Your people are your greatest asset—consistent training is essential to performance, retention, and trust.",
      features: [
        "Operational excellence and daily execution training",
        "Leadership habits that drive consistency and accountability",
        "Skill reinforcement that cuts through day-to-day distractions",
        "Empowering employees to take ownership of their roles"
      ],
      tagline: "Excellence is built through continual reinforcement that turns skills into habits."
    },
    {
      id: "leadership",
      icon: "Users",
      title: "Leadership Support & Delegation",
      description: "Many executives carry too much operational weight, limiting strategic focus and growth.",
      features: [
        "Identify tasks that interrupt strategic momentum",
        "Develop effective delegation frameworks",
        "Build trust through clarity, communication, and training",
        "Strengthen leadership capacity at all levels"
      ],
      tagline: "As clarity increases, execution improves—and teams gain confidence."
    },
    {
      id: "product",
      icon: "Monitor",
      title: "Product Development Review",
      description: "The world of technology brings many options for supporting the apartment industry today.",
      features: [
        "Review products and how they fit into daily operational workflow",
        "Refine sales presentations to focus on product benefits",
        "Provide ideas on how to capitalize on industry needs",
        "Evaluate technology solutions for operational fit"
      ],
      tagline: "Navigate technology options with confidence."
    }
  ]
};

export const resultsData = {
  title: "The Results You Can Expect",
  subtitle: "When your team is aligned, trained, and empowered, performance follows",
  results: [
    {
      icon: "CheckCircle",
      title: "Operational Consistency",
      description: "Improved efficiency and standardized processes across your organization"
    },
    {
      icon: "Award",
      title: "Confident Leadership",
      description: "Capable leadership teams who operate with clarity and purpose"
    },
    {
      icon: "Heart",
      title: "Employee Engagement",
      description: "Increased accountability and team ownership of roles"
    },
    {
      icon: "Compass",
      title: "Strategic Space",
      description: "More time for strategic thinking and growth initiatives"
    },
    {
      icon: "Star",
      title: "Customer Excellence",
      description: "Enhanced customer experience through stronger execution"
    }
  ]
};

export const aboutChristinaData = {
  name: "Christina Clark",
  title: "Founder & Principal Consultant",
  image: "https://customer-assets.emergentagent.com/job_clark-redesign/artifacts/1xoozahs_christina.jpg",
  bio: "With over 40 years of hands-on property management experience, I understand the pressures of peak workload periods, rapid growth, and limited resources—and how to navigate them effectively. My journey spans interactions with employees, owners, and industry suppliers, developing extensive knowledge of operational excellence.",
  credentials: [
    "Certified Property Manager (CPM®) - Institute of Real Estate Management",
    "Change Management Certificate - Cornell University (eCornell, 2022)",
    "California Real Estate License",
    "Former President - Nevada Apartment Association",
    "Author of multiple operations manuals and training programs"
  ],
  quote: "Training is not a one-time event. Excellence is built through continual reinforcement that turns skills into habits."
};

export const statsData = [
  { value: "40+", label: "Years Experience" },
  { value: "500+", label: "Properties Transformed" },
  { value: "1000+", label: "Professionals Trained" },
  { value: "98%", label: "Client Satisfaction" }
];

export const contactData = {
  title: "Let's Build the Next Phase of Your Organization",
  subtitle: "If you're a property management executive looking to strengthen operations, develop your people, and create sustainable momentum, I'm ready to support you.",
  cta: "Schedule a strategic consultation and explore how we can support your organization's goals.",
  email: "Christina@ccconsulting3.com",
  phone: "916-899-3408"
};

export const navigationData = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "Results", href: "#results" },
  { label: "About", href: "#about" },
  { label: "Gallery", href: "#gallery" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" }
];

export const faqData = {
  title: "Frequently Asked Questions",
  subtitle: "Get answers to common questions about our property management consulting services",
  faqs: [
    {
      question: "What types of property management companies do you work with?",
      answer: "We work with multi-family property management companies of all sizes, from boutique firms managing a handful of properties to large organizations with extensive portfolios. We also partner with industry suppliers and technology providers serving the apartment industry. Our services are customized to meet each client's unique operational needs and growth objectives."
    },
    {
      question: "How long does a typical consulting engagement last?",
      answer: "Engagement length varies based on scope and objectives. Strategic planning projects typically span 2-4 weeks, while comprehensive operational reviews may take 4-8 weeks. Training programs can be delivered as single sessions or ongoing development series. We work with you to design an engagement timeline that fits your schedule and delivers meaningful results."
    },
    {
      question: "What makes your training approach different from other consultants?",
      answer: "Our training philosophy is built on 40+ years of hands-on property management experience. We believe training is not a one-time event—excellence is built through continual reinforcement that turns skills into habits. Our programs are customized to reflect your organization's culture, systems, and expectations, focusing on practical skills that drive daily performance."
    },
    {
      question: "Can you help with technology selection and implementation?",
      answer: "Yes, through our Product Development Review service, we help organizations evaluate technology solutions for operational fit. We review how products integrate into daily workflows, assess sales presentations for benefit clarity, and provide guidance on capitalizing on industry technology trends. Our goal is to help you navigate technology options with confidence."
    },
    {
      question: "What results can we expect from working with Christina Clark Consulting?",
      answer: "Clients typically experience improved operational consistency and efficiency, more confident and capable leadership teams, increased employee engagement and accountability, more time for strategic thinking and growth initiatives, and enhanced customer experience through stronger execution. When your team is aligned, trained, and empowered, performance follows."
    },
    {
      question: "Do you offer virtual or remote consulting services?",
      answer: "Yes, we offer both on-site and virtual consulting options. Many of our strategic planning sessions, training programs, and operational reviews can be conducted remotely via video conferencing. We work with clients nationwide and adapt our delivery method to best serve your team's needs and preferences."
    },
    {
      question: "How do you customize services for each client?",
      answer: "Every engagement begins with understanding your organization's unique challenges, culture, and objectives. We don't use one-size-fits-all solutions. Instead, we develop practical policies, procedures, and training programs that align with your specific business goals, team dynamics, and operational realities. This personalized approach ensures relevant, actionable outcomes."
    },
    {
      question: "What is your approach to leadership development?",
      answer: "We help executives and managers identify tasks that interrupt strategic momentum, develop effective delegation frameworks, and build trust through clarity, communication, and training. Our focus is on strengthening leadership capacity at all levels so that as clarity increases, execution improves—and teams gain confidence and engagement."
    }
  ]
};

export const galleryData = {
  title: "Our Community",
  subtitle: "Building connections and celebrating success together",
  images: [
    {
      id: 1,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/nofh14ra_img%207731%20topgolf.jpg",
      alt: "Team outing at TopGolf 2025",
      caption: "Team Event at TopGolf 2025"
    },
    {
      id: 2,
      src: "https://customer-assets.emergentagent.com/job_clark-redesign/artifacts/u83em4bj_img0666.jpg",
      alt: "Team celebration at restaurant",
      caption: "Team Celebration"
    },
    {
      id: 3,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/46krlsif_img2403%20team%20outing.jpg",
      alt: "Team outing at shopping village",
      caption: "Team Outing"
    },
    {
      id: 4,
      src: "https://customer-assets.emergentagent.com/job_clark-redesign/artifacts/nvc5k1tg_img2424.jpg",
      alt: "Team collaboration meeting",
      caption: "Team Collaboration"
    },
    {
      id: 5,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/8dmkfyjo_img9315.jpg",
      alt: "Large team group photo in office lobby",
      caption: "Team Meeting"
    },
    {
      id: 6,
      src: "https://customer-assets.emergentagent.com/job_clark-redesign/artifacts/uobruxgr_3209.jpg",
      alt: "Team gathering at beach",
      caption: "Beach Team Gathering"
    },
    {
      id: 6,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/se2oyt06_img4112.jpg",
      alt: "Team members at outdoor event with dog",
      caption: "Community Day Event"
    },
    {
      id: 7,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/6v6xazrb_Breast%20Cancer%20walk.jpg",
      alt: "Tomanek Rack Pack at breast cancer charity walk",
      caption: "Breast Cancer Charity Walk"
    },
    {
      id: 8,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/axoswv8x_Christina%20NVAA%201-92.jpg",
      alt: "Christina at Nevada Apartment Association event 1992",
      caption: "NAA Event - 1992"
    },
    {
      id: 9,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/62g0sm4y_Doug%2C%20Kat%2C%20Christina.jpg",
      alt: "Industry colleagues at formal event",
      caption: "Industry Gala"
    },
    {
      id: 10,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/1ii08you_img0990.jpg",
      alt: "RealWorld 2022 Conference Event",
      caption: "RealWorld 2022"
    },
    {
      id: 11,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/26d1tqas_img4526.jpg",
      alt: "Conference event with R2D2",
      caption: "Industry Conference"
    },
    {
      id: 12,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/m5fh0kcj_IMG_1477%20-%20Copy.jpg",
      alt: "Race for the Cure Hero Survivor medal 2016",
      caption: "Hero Survivor - Race for the Cure 2016"
    },
    {
      id: 13,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/ltl0rs4w_IMG_1476.jpg",
      alt: "Tomanek Rack Pack team at Race for the Cure",
      caption: "Tomanek Rack Pack - Race for the Cure"
    },
    {
      id: 14,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/87w86tf7_IMG_1108%20-%20Copy.jpg",
      alt: "Leadership training materials and workbook",
      caption: "Leadership Training Materials"
    },
    {
      id: 15,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/7vwmrugg_IMG_0427.jpg",
      alt: "Communities Making a Difference shirt",
      caption: "Communities Making a Difference"
    },
    {
      id: 16,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/x51ha9d0_img1943.jpg",
      alt: "Tomanek 10 Years Anniversary Celebration",
      caption: "Tomanek 10 Years Anniversary"
    },
    {
      id: 17,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/pkg71voi_img05686.jpg",
      alt: "Team dinner at waterfront restaurant",
      caption: "Waterfront Team Dinner"
    },
    {
      id: 18,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/pzdcq3z3_romanek%20retreat%202018.jpg",
      alt: "Tomanek Retreat 2018 group photo",
      caption: "Tomanek Retreat 2018"
    },
    {
      id: 19,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/qp2jmtyy_img0275.png",
      alt: "Nautical themed event setup",
      caption: "Event Setup"
    },
    {
      id: 20,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/sqn3jxhj_award%20celebration1.jpg",
      alt: "Team Award Celebration group photo",
      caption: "Award Celebration"
    },
    {
      id: 21,
      src: "https://customer-assets.emergentagent.com/job_c37f8639-afef-44f7-8aa2-b06d370dacb2/artifacts/b5jcxawg_img3878.jpg",
      alt: "Team gathering in wine cave",
      caption: "Wine Cave Team Gathering"
    }
  ]
};

export const footerData = {
  company: "Christina Clark Consulting",
  tagline: "Strategic Operations & Training Solutions",
  copyright: `© ${new Date().getFullYear()} Christina Clark Consulting. All rights reserved.`
};
